import { clsx } from 'clsx'
import { useId } from 'react'

/**
 * A bordered card that collapses to a one-glance summary and expands to its full editor. The whole summary
 * is the toggle; `actions` (Copy to…, Remove) only show while it's open, beside the + / × in the corner, or on a
 * phone as the first line of the open card, so they don't squeeze the summary into a narrow column.
 * `summary` sits inside a button, so it must be inline content (spans, not divs).
 */
export function Collapsible({ label, open, onToggle, summary, actions, children }: { label: string; open: boolean; onToggle: () => void; summary: React.ReactNode; actions?: React.ReactNode; children: React.ReactNode }) {
  const bodyId = useId()
  return (
    <section aria-label={label} className={clsx('grid border border-gray-200', open && 'gap-5 pb-5')}>
      <div className="flex items-start gap-3">
        <button type="button" aria-expanded={open} aria-controls={bodyId} onClick={onToggle} className="flex min-w-0 flex-1 items-start p-5 pr-0 text-left">
          {summary}
        </button>
        <div className="flex items-center gap-4 p-5 pl-0">
          {open && actions && <div className="flex items-center gap-4 max-sm:hidden">{actions}</div>}
          <button type="button" onClick={onToggle} aria-label={open ? `Collapse ${label}` : `Expand ${label}`} aria-controls={bodyId} className="text-dim hover:text-ink">
            <span aria-hidden="true" className={clsx('inline-block transition-transform', open && 'rotate-45')}>
              +
            </span>
          </button>
        </div>
      </div>
      {open && (
        <div id={bodyId} className="grid gap-5 px-5">
          {actions && <div className="-mt-2 flex flex-wrap items-center gap-x-5 gap-y-2 sm:hidden">{actions}</div>}
          {children}
        </div>
      )}
    </section>
  )
}
